import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getElements,
  reduceElements,
} from "core-integration/src/store/elements/reducers";
import { showElementSummary } from "core-integration/src/store/elements/thunks";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { fetchMyTeam } from "core-integration/src/store/my-team/thunks";
import {
  getPlayerData,
  getWatched,
} from "core-integration/src/store/player/reducers";
import { getSavedPicks } from "core-integration/src/store/squad/reducers";
import { getElementPriceChanges } from "core-integration/src/store/stats/reducers";
import { fetchElementPriceChanges } from "core-integration/src/store/stats/thunks";
import { getTeamsById } from "core-integration/src/store/teams/reducers";
import { SearchField } from "plos/src/components/forms/SearchField";
import { SearchFieldLabels } from "plos/src/components/forms/SearchField/types";
import PageTitle from "plos/src/components/PageTitle";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { pagePadding } from "plos/src/layouts";
import { titleContainer } from "plos/src/styles/utils.css";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTrackingContext } from "../../contexts/TrackingContext";
import { HelmetHead } from "../HelmetHead";
import { PredictionsTable } from "./PredictionsTable";
import {
  PriceChangesViewToggle,
  usePriceChangeControls,
} from "./PriceChangeControls";
import { PriceChangeFilters } from "./PriceChangeFilters";
import {
  filterByProgress,
  filterPriceChangeHistory,
  matchesElementFilter,
  sortElementsByColumn,
  sortPriceChangeHistoryByColumn,
} from "./PriceChangeFilters/utils";
import styles from "./priceChanges.module.css";
import { RecentChangesTable } from "./RecentChangesTable";
import { PriceChangesTableProps } from "./types";
import { UnderstandingPriceChanges } from "./UnderstandingPriceChanges";

const PriceChanges = () => {
  const { t } = useTranslation("priceChanges");
  const { firePageViewEvent } = useTrackingContext();
  const dispatch = useDispatch<ThunkDispatch>();

  const {
    view: { activeView, handleViewChange },
    filters,
    sort,
    pagination,
    search,
  } = usePriceChangeControls();
  const { progressFilter, elementFilter } = filters;
  const { sortDescriptor } = sort;

  const player = useSelector(getPlayerData);
  const entry = useSelector((state: RootState) =>
    player?.entry ? getEntry(state, player.entry) : null,
  );
  const currentEvent = useSelector(getCurrentEvent);

  const currencyDivisor = 10;
  const teamsById = useSelector(getTeamsById);
  const showElementDialog = (elementId: number) => {
    dispatch(showElementSummary(elementId));
  };

  useEffect(() => {
    firePageViewEvent("fantasy priceChanges");
  }, []);

  useEffect(() => {
    dispatch(fetchMyTeam());
  }, [dispatch, entry]);

  useEffect(() => {
    dispatch(fetchElementPriceChanges());
  }, [dispatch]);

  const watched = useSelector(getWatched);

  const allElements = useSelector(getElements);
  const elements = useMemo(
    () =>
      reduceElements(
        allElements.filter((e) => activeView === "history" || e.status !== "u"),
        search.value,
        0,
      ),
    [allElements, activeView, search.value],
  );
  const savedPicks = useSelector(getSavedPicks);
  const savedPicksByElement = useMemo(
    () =>
      Object.values(savedPicks).reduce<
        Record<number, (typeof savedPicks)[string]>
      >((acc, pick) => {
        acc[pick.element] = pick;
        return acc;
      }, {}),
    [savedPicks],
  );

  const priceChangeElements = useMemo(
    () =>
      sortElementsByColumn(
        filterByProgress(
          elements.filter((e) =>
            matchesElementFilter(e, elementFilter, {
              savedPicksByElement,
              watched,
            }),
          ),
          progressFilter,
        ),
        sortDescriptor,
        savedPicksByElement,
      ),
    [
      elements,
      elementFilter,
      progressFilter,
      savedPicksByElement,
      watched,
      sortDescriptor,
    ],
  );

  const elementsById = useMemo(
    () => Object.fromEntries(elements.map((e) => [e.id, e])),
    [elements],
  );
  const priceChangeHistory = useSelector(getElementPriceChanges);
  const recentPriceChanges = useMemo(
    () =>
      sortPriceChangeHistoryByColumn(
        filterPriceChangeHistory(priceChangeHistory, {
          elementsById,
          elementFilter,
          progressFilter,
          savedPicksByElement,
          watched,
        }),
        sortDescriptor,
        elementsById,
      ),
    [
      priceChangeHistory,
      elementsById,
      elementFilter,
      progressFilter,
      savedPicksByElement,
      watched,
      sortDescriptor,
    ],
  );

  const labels: SearchFieldLabels = {
    label: t("squad:elementList.searchLabel", "Find a player") as string,
    placeholder: t(
      "squad:elementList.searchPlaceholder",
      "Search by name",
    ) as string,
  };

  const sharedTableProps: PriceChangesTableProps = {
    showElementDialog,
    teamsById,
    currencyDivisor,
    handleSortChange: sort.onSortChange,
    sortDescriptor,
    page: pagination.page,
    setPage: pagination.setPage,
  };

  return (
    <div className={pagePadding}>
      <HelmetHead
        title={t("metadata.title", "Price Changes | Fantasy Premier League")}
        description={t(
          "metadata.description",
          "View upcoming player price changes in Fantasy Premier League.",
        )}
      />
      <div className={styles.statsWrapper}>
        <div className={titleContainer}>
          <PageTitle title={t("title", "Price Changes")} />
        </div>
        {priceChangeHistory.length > 0 && (
          <PriceChangesViewToggle
            selected={activeView}
            handleViewChange={handleViewChange}
          />
        )}
        <SearchField
          id="search"
          name="search"
          labels={labels}
          onChange={search.onChange}
          value={search.value}
        />
        <div className={styles.controlsWrapper}>
          <PriceChangeFilters
            handleResetFilters={filters.onReset}
            hasEntry={Boolean(player?.entry)}
            progressFilter={progressFilter}
            onProgressFilterChange={filters.onProgressFilterChange}
            elementFilter={elementFilter}
            onElementFilterChange={filters.onElementFilterChange}
            isSortModified={sort.isSortModified}
            isSearchModified={Boolean(search.value)}
            showLockedOption={activeView === "predictions"}
          />
          <UnderstandingPriceChanges />
        </div>
        <SurfaceContainer>
          {activeView === "predictions" && (
            <PredictionsTable
              {...sharedTableProps}
              hasEntry={Boolean(player?.entry)}
              priceChangeElements={priceChangeElements}
              currentEvent={currentEvent}
              savedPicksByElement={savedPicksByElement}
            />
          )}
          {activeView === "history" && (
            <RecentChangesTable
              {...sharedTableProps}
              recentPriceChanges={recentPriceChanges}
              elementsById={elementsById}
            />
          )}
        </SurfaceContainer>
      </div>
    </div>
  );
};

export default PriceChanges;
